/**
 * AnnouncementsSection — Avisos del administrador (MOTD) para terapeutas.
 * Diseño: Isabel Moreno (UX/UI Lead) · Implementación: Sofía García (Frontend)
 */
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { announcementsAPI } from 'resources/api';
import { DARK_TEXT, ERROR_RED, WARNING_ORANGE, INFO_BLUE, BRAND_GREEN_PRIMARY } from 'resources/constants/colors';

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled.div`
    animation: ${fadeUp} 0.35s 0.15s ease both;
`;

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 8px;
    padding: 0 2px;
`;

const Badge = styled.span`
    background: ${ERROR_RED};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    border-radius: 10px;
    padding: 1px 7px;
    margin-left: 6px;
    vertical-align: middle;
`;

const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 12px 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 60px;
    justify-content: center;
`;

const AnnouncementItem = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid #f2f2f2;
    &:last-child { border-bottom: none; }
`;

const IconWrap = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    background: ${({ bg }) => bg || '#f5f5f5'};
`;

const Content = styled.div`
    flex: 1;
    min-width: 0;
`;

const Title = styled.p`
    font-size: 13px;
    font-weight: 700;
    color: #222;
    margin: 0 0 2px;
`;

const Body = styled.p`
    font-size: 12px;
    color: ${DARK_TEXT};
    margin: 0;
    line-height: 1.5;
`;

const Meta = styled.p`
    font-size: 10px;
    color: #bbb;
    margin: 3px 0 0;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 10px;
`;

const EmptyText = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    margin: 4px 0 0;
`;

const typeConfig = {
    info:    { icon: '📢', bg: `${INFO_BLUE}18` },
    warning: { icon: '⚠️', bg: `${WARNING_ORANGE}18` },
    urgent:  { icon: '🚨', bg: `${ERROR_RED}18` },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AnnouncementsSection = () => {
    const [list, setList] = useState([]);

    useEffect(() => {
        announcementsAPI.getAll()
            .then(res => setList(Array.isArray(res) ? res : []))
            .catch(() => setList([]));
    }, []);

    const urgentCount = list.filter(a => a.type === 'urgent').length;

    return (
        <Section>
            <SectionLabel>
                Avisos
                {urgentCount > 0 && <Badge>{urgentCount}</Badge>}
            </SectionLabel>
            <Card>
                {list.length === 0 ? (
                    <EmptyState>
                        <span style={{ fontSize: 26 }}>📭</span>
                        <EmptyText>Sin avisos recientes</EmptyText>
                    </EmptyState>
                ) : (
                    list.map(item => {
                        const config = typeConfig[item.type] || typeConfig.info;
                        return (
                            <AnnouncementItem key={item.id}>
                                <IconWrap bg={config.bg}>{config.icon}</IconWrap>
                                <Content>
                                    <Title>{item.title}</Title>
                                    <Body>{item.body}</Body>
                                    <Meta>{formatDate(item.createdAt)}</Meta>
                                </Content>
                            </AnnouncementItem>
                        );
                    })
                )}
            </Card>
        </Section>
    );
};

export default AnnouncementsSection;

